import { useNavigate, useParams } from "react-router-dom";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import MapComponent from "../../../../common/components/map/map.component";
import MapMarker from "../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../common/components/map/types/map-route";
import useAbort from "../../../../common/hooks/use-abort";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import OrderOptimizerRoadRouteParams from "../../../../common/services/order-optimizer/road-route/order-optimizer-road-route-params";
import useOrderOptimizerRoadRoute from "../../../../common/services/order-optimizer/road-route/use-order-optimizer-road-route";
import notificationService from "../../../../common/utils/notification/notification.service";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import orderOptimizerTranslationsHelper from "../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerDetailsBreadcrumbsParams from "../../common/breadcrumbs/types/order-optimizer-details-breadcrumbs-params";
import { useEffect, useMemo, useState } from "react";
import orderOptimizerCargoDetailsItemFactory from "./common/order-optimizer-cargo-details-item.factory";
import orderOptimizerCargoDetailsWaypointFactory from "./common/order-optimizer-cargo-details-waypoint.factory";
import OrderOptimizerCargoDetailsBeforeOptimizationTableComponent from "./before-optimization-table/order-optimizer-cargo-details-before-optimization-table.component";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import orderOptimizerCargoDetailsRouteFactory from "./common/order-optimizer-cargo-details-route.factory";
import orderOptimizerHelper from "../../common/order-optimizer.helper";
import orderOptimizerCargoDetailsMapMarkersFactory from "./common/order-optimizer-cargo-details-map-markers.factory";
import orderOptimizerBreadcrumbsHelper from "../../common/breadcrumbs/order-optimizer-breadcrumbs.helper";
import OrderOptimizerDetailsWaypoint from "../../common/types/order-optimizer-details-waypoint";
import OrderOptimizerCargoDetailsOrdersTableComponent from "./orders-table/order-optimizer-cargo-details-orders-table.component";
import orderOptimizerRoutesHelper from "../../common/routes/order-optimizer-routes.helper";
import OrderOptimizerCargoDetailsHiddenDetailsComponent from "./hidden-details/order-optimizer-cargo-details-hidden-details.component";
import useOrderOptimizerDetailsCargo from "../../../../common/services/order-optimizer/details/cargo/use-order-optimizer-details-cargo";
import OrderOptimizerDetailsCargoParams from "../../../../common/services/order-optimizer/details/cargo/order-optimizer-details-cargo-params";
import { OrderOptimizerCargoDetailsItemOrders } from "./common/types/order-optimizer-cargo-details-item";
import orderOptimizerCargoDetailsHelper from "./order-optimizer-cargo-details.helper";

const OrderOptimizerCargoDetailsComponent = () => {
  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();
  const { id, excludedOrders } =
    useParams<OrderOptimizerDetailsBreadcrumbsParams>();
  const roadRoute = useOrderOptimizerRoadRoute();
  const translation =
    orderOptimizerTranslationsHelper.getCargoDetailsTranslations();

  const orderOptimizerDetails = useOrderOptimizerDetailsCargo();
  const orderOptimizerDetailsAbort = useAbort();

  const orderOptimizerLocalDetails = useOrderOptimizerDetailsCargo();
  const orderOptimizerLocalDetailsAbort = useAbort();

  const [waypoints, setWaypoints] = useState<OrderOptimizerDetailsWaypoint[]>(
    [],
  );

  const navigate = useNavigate();
  const [homeMarker, setHomeMarker] = useState<MapMarker | null>(null);
  const [homeRoutes, setHomeRoutes] = useState<MapRoute[]>([]);

  const [mapRoutes, setMapRoutes] = useState<MapRoute[]>([]);

  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const [hiddenOrders, setHiddenOrders] = useState<number[]>([]);

  const [localDetailsItem, setLocalDetailsItem] = useState<
    OrderOptimizerCargoDetailsItemOrders[]
  >([]);
  const [taxiDriverAssociationUuid, setTaxiDriverAssociationUuid] =
    useState<string>();

  useEffect(() => {
    if (!excludedOrders || excludedOrders === "all-orders") {
      setHiddenOrders([]);
    } else {
      const updatedOrders = excludedOrders.split(",").map((hiddenOrderId) => {
        return Number(hiddenOrderId);
      });

      setHiddenOrders(updatedOrders);
    }
  }, [excludedOrders]);

  useEffect(() => {
    if (orderOptimizerDetails.data?.orders) {
      setLocalDetailsItem(
        orderOptimizerCargoDetailsItemFactory.createDetailsItemOrders(
          orderOptimizerDetails.data.orders,
        ),
      );
    }
  }, [orderOptimizerDetails.data?.orders]);

  const onOrderVisibilityClick = (orderId: number) => {
    const updatedOrders = hiddenOrders.includes(orderId)
      ? hiddenOrders.filter((id) => id !== orderId)
      : [...hiddenOrders, orderId];

    navigate(
      orderOptimizerRoutesHelper.getDetailsRoute({
        id: id!,
        excludedOrders:
          updatedOrders.length > 0 ? updatedOrders.join(",") : "all-orders",
      }),
      { replace: true },
    );

    loadOrderOptimizerLocalDetails(
      id!,
      updatedOrders,
      taxiDriverAssociationUuid,
    );
  };

  const loadOrderOptimizerDetails = (
    optimizationId: string,
    excludedOrders?: number[],
    taxiDriverAssociationId?: string,
  ) => {
    const params: OrderOptimizerDetailsCargoParams = {
      optimizationId: optimizationId,
      taxiDriverAssociationId: taxiDriverAssociationId,
      excludedOrders: excludedOrders,
    };

    orderOptimizerDetails.load(params, orderOptimizerLocalDetailsAbort.signal);
  };

  const loadOrderOptimizerLocalDetails = (
    optimizationId: string,
    excludedOrders?: number[],
    taxiDriverAssociationId?: string,
  ) => {
    const params: OrderOptimizerDetailsCargoParams = {
      optimizationId: optimizationId,
      taxiDriverAssociationId: taxiDriverAssociationId,
      excludedOrders: excludedOrders,
    };

    orderOptimizerLocalDetails.load(params, orderOptimizerDetailsAbort.signal);
  };

  useEffect(() => {
    if (!id) return;

    loadOrderOptimizerDetails(id, hiddenOrders);

    return orderOptimizerDetailsAbort.revoke;
  }, []);

  const detailsItem = useMemo(() => {
    if (orderOptimizerLocalDetails.data) {
      return orderOptimizerCargoDetailsItemFactory.createDetailsItem(
        orderOptimizerLocalDetails.data,
      );
    }

    if (orderOptimizerDetails.data) {
      return orderOptimizerCargoDetailsItemFactory.createDetailsItem(
        orderOptimizerDetails.data,
      );
    }

    return undefined;
  }, [orderOptimizerDetails.data, orderOptimizerLocalDetails.data]);

  useEffect(() => {
    if (!detailsItem || roadRoute.data.length > 0) return;

    const preparedWaypoints =
      orderOptimizerCargoDetailsWaypointFactory.createWaypoints(
        detailsItem,
        hiddenOrders,
      );

    setWaypoints(preparedWaypoints.flat());

    const loadParams: OrderOptimizerRoadRouteParams[] = [];
    preparedWaypoints.forEach((route) => {
      if (route.length > 1) {
        const request =
          orderOptimizerCargoDetailsWaypointFactory.createSearchRoutingRequest(
            route,
          );
        loadParams.push(request);
      }
    });

    roadRoute.load(
      loadParams,
      preparedWaypoints.map((waypoints) => waypoints[0].humanId),
    );
  }, [detailsItem, hiddenOrders]);

  useEffect(() => {
    const breadcrumbs = orderOptimizerBreadcrumbsHelper.getDetailsBreadcrumbs({
      id: id!,
      excludedOrders: "",
    });
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const translations =
    orderOptimizerTranslationsHelper.getCargoDetailsTranslations();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(
    documentTitleTranslations.orderOptimizerDetails.replace("#{id}", id!),
  );

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers =
      orderOptimizerCargoDetailsMapMarkersFactory.createMapMarkers(
        waypoints,
        hiddenOrders,
      );
    return mapMarkers;
  }, [waypoints, hiddenOrders]);

  useEffect(() => {
    if (!roadRoute.data) return;

    const routes = roadRoute.data
      .filter((routeItem) => !hiddenOrders.includes(routeItem.orderHumanId))
      .map((route) =>
        orderOptimizerCargoDetailsRouteFactory.createMapRoute(
          route.coordinates,
          orderOptimizerHelper.getOrderColor(
            localDetailsItem.findIndex(
              (details) => details.humanId === route.orderHumanId,
            ),
          ),
        ),
      );
    setMapRoutes(routes);
  }, [roadRoute.data, hiddenOrders]);

  const handleHideDetails = () => {
    setDetailsExpanded(false);
  };

  const handleShowDetails = () => {
    setDetailsExpanded(true);
  };

  useEffect(() => {
    if (!id) return;
    loadOrderOptimizerDetails(id, [], taxiDriverAssociationUuid);
    orderOptimizerLocalDetails.reset();
    setHiddenOrders([]);
  }, [taxiDriverAssociationUuid]);

  useEffect(() => {
    if (orderOptimizerDetails.isError) {
      notificationService.error(translation.noDriverContractErrorNotification);
    }
  }, [orderOptimizerDetails.isError]);

  const scoreLabel = orderOptimizerCargoDetailsHelper.getScoreLabel(
    detailsItem?.score,
  );

  const idLabel = orderOptimizerCargoDetailsHelper.getIdLabel(Number(id));

  return (
    <>
      <HeadingComponent
        title={orderOptimizerCargoDetailsHelper.getHeadingText(id!)}
      />
      <CardComponent>
        <div className="order_optimizer_cargo_details">
          <div className="order_optimizer_cargo_details__left">
            <div className="order_optimizer_cargo_details_map_wrapper">
              <MapComponent
                markers={homeMarker ? [...mapMarkers, homeMarker] : mapMarkers}
                routes={[...mapRoutes, ...homeRoutes]}
                autoFitOnUpdate
              />
            </div>
            {detailsExpanded ? (
              <ButtonComponent
                onClick={handleHideDetails}
                classNames={{
                  root: "order_optimizer_cargo_details__left__button",
                }}
                title={translations.hideDetailsButtonTitle}
                type="primary"
              >
                {translations.hideDetailsButtonLabel}
                <FontAwesomeIcon
                  icon={faAngleUp}
                  className="order_optimizer_cargo_details__left__button__icon"
                />
              </ButtonComponent>
            ) : (
              <ButtonComponent
                onClick={handleShowDetails}
                classNames={{
                  root: "order_optimizer_cargo_details__left__button",
                }}
                title={translations.showDetailsButtonTitle}
                type="primary"
                isDisabled={!detailsItem}
              >
                {translations.showDetailsButtonLabel}
                <FontAwesomeIcon
                  icon={faAngleDown}
                  className="order_optimizer_cargo_details__left__button__icon"
                />
              </ButtonComponent>
            )}
          </div>
          <div className="order_optimizer_cargo_details__right">
            <div className="order_optimizer_cargo_details__right__info">
              <div className="order_optimizer_cargo_details__right__info__left">
                <h1 className="order_optimizer_cargo_details__right__info__left__text">
                  {scoreLabel}
                </h1>
                <h1 className="order_optimizer_cargo_details__right__info__left__text">
                  {idLabel}
                </h1>
              </div>
              <div className="order_optimizer_cargo_details__right__info__right">
                {!!detailsItem?.reviewedAt && !!detailsItem.reviewedBy && (
                  <div
                    className="order_optimizer_cargo_details__right__info__right__badge"
                    title={`${
                      detailsItem?.reviewedBy
                    }, ${orderOptimizerCargoDetailsHelper.getDateLabel(
                      detailsItem?.reviewedAt ?? null,
                    )}`}
                  >
                    {detailsItem?.reviewedBy},{" "}
                    {orderOptimizerCargoDetailsHelper.getDateLabel(
                      detailsItem?.reviewedAt ?? null,
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="order_optimizer_cargo_details_orders_table">
              <OrderOptimizerCargoDetailsOrdersTableComponent
                detailsOrderItems={localDetailsItem}
                hiddenOrders={hiddenOrders}
                onOrderVisibilityClick={onOrderVisibilityClick}
              />
            </div>
            <div className="order_optimizer_cargo_details_before_optimization_table">
              <OrderOptimizerCargoDetailsBeforeOptimizationTableComponent
                detailsOptimizedStatsItem={detailsItem?.optimizedStats}
                detailsUnoptimizedStatsItem={detailsItem?.unoptimizedStats}
                detailsOrderItems={detailsItem?.orders}
              />
            </div>
          </div>
        </div>
        {detailsExpanded && !!detailsItem && (
          <OrderOptimizerCargoDetailsHiddenDetailsComponent
            optimizationId={id}
            detailsItem={detailsItem}
          />
        )}
      </CardComponent>
    </>
  );
};

export default OrderOptimizerCargoDetailsComponent;
